import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getUserFamilyInfo, getFamilyMembers } from '../services/familyService';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { participantEvents } from '../utils/participantEvents';

// Helper function to get user name from Firebase users collection
const getUserName = async (userId, fallbackEmail) => {
  try {
    const userDoc = await getDoc(doc(db, 'users', userId));
    if (userDoc.exists()) {
      const userData = userDoc.data();
      if (userData.firstName && userData.lastName) {
        return `${userData.firstName} ${userData.lastName}`;
      } else if (userData.firstName) {
        return userData.firstName;
      } else if (userData.lastName) {
        return userData.lastName;
      }
    }
    // Fallback to email if no name found
    return fallbackEmail ? fallbackEmail.split('@')[0] : 'User';
  } catch (error) {
    console.error('Error fetching user name:', error);
    return fallbackEmail ? fallbackEmail.split('@')[0] : 'User';
  }
};

export const useFamilyParticipants = () => {
  const { currentUser } = useAuth();
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Function to manually refresh participants
  const refreshParticipants = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  useEffect(() => {
    if (!currentUser) {
      // Default participants when no user is logged in
      setParticipants([
        { id: 'user1', name: 'Me' },
        { id: 'child', name: 'Child' }
      ]);
      setLoading(false);
      return;
    }

    const fetchFamilyParticipants = async () => {
      try {
        setLoading(true);
        const userFamilyInfo = await getUserFamilyInfo(currentUser.uid);
        
        if (userFamilyInfo?.familyId) {
          // User has a family, get family members
          const familyMembers = await getFamilyMembers(userFamilyInfo.familyId);
          
          // Convert family members to participant format with actual names
          const familyParticipants = await Promise.all(
            familyMembers.map(async (member) => {
              const displayName = await getUserName(member.userId, member.email);
              return {
                id: member.userId,
                name: displayName, // Use actual name for display
                email: member.email, // Keep email for linking/filtering
                role: member.role
              };
            })
          );

          // Get family data to access custom participants
          const familyDoc = await getDoc(doc(db, 'families', userFamilyInfo.familyId));
          const familyData = familyDoc.data();
          const customParticipants = familyData?.customParticipants || [];

          // Filter out custom participants who have become real family members
          // This prevents duplicates when someone accepts an invitation
          const familyEmails = new Set(familyParticipants.map(member => member.email.toLowerCase()));
          const activeCustomParticipants = customParticipants.filter(customParticipant => {
            // Keep custom participants that haven't been invited yet
            if (!customParticipant.invited) {
              return true;
            }
            
            // Filter out custom participants whose invited email matches a family member
            if (customParticipant.invitedEmail && familyEmails.has(customParticipant.invitedEmail.toLowerCase())) {
              console.log(`🔄 Filtering out custom participant "${customParticipant.name}" - now a family member (${customParticipant.invitedEmail})`);
              return false;
            }
            
            // Keep custom participants who were invited but haven't joined yet
            return true;
          });

          // Combine family members and active custom participants
          const allParticipants = [...familyParticipants, ...activeCustomParticipants];

          console.log('👨‍👩‍👧‍👦 All participants loaded:', allParticipants);
          setParticipants(allParticipants);
        } else {
          // User doesn't have a family, get user's actual name and show with Child
          const currentUserName = await getUserName(currentUser.uid, currentUser.email);
          setParticipants([
            { id: currentUser.uid, name: currentUserName },
            { id: 'child', name: 'Child' }
          ]);
        }
      } catch (error) {
        console.error('Error fetching family participants:', error);
        // Fallback to show only current user and child
        const fallbackUserName = currentUser.displayName || currentUser.email?.split('@')[0] || 'Me';
        setParticipants([
          { id: currentUser.uid, name: fallbackUserName },
          { id: 'child', name: 'Child' }
        ]);
      } finally {
        setLoading(false);
      }
    };

    fetchFamilyParticipants();
  }, [currentUser, refreshTrigger]);

  // Listen for global participant updates
  useEffect(() => {
    const unsubscribe = participantEvents.subscribe(() => {
      refreshParticipants();
    });
    return unsubscribe;
  }, []);

  return {
    participants,
    loading,
    refreshParticipants
  };
};